import {
  Component,
  OnDestroy,
  OnInit,
  NgZone,
  PLATFORM_ID,
  Inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  Router,
  NavigationEnd,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { NgClass, NgIf } from '@angular/common';
import { AuthService } from '../../../core/services/auth/auth.service';
import { SharedPipesModule } from '../../pipes/shared-pipes.module';
import { ModuleIds } from '../../../core/constants/module-ids';
import { filter, Subject, takeUntil, fromEvent, timer } from 'rxjs';
import { HrAuthServiceService } from '../../../core/services/hr_auth/hr-auth-service.service';
import { SidebarStateService } from '../../services/sidebar-state.service';
import { log } from 'node:console';
import { ToastrService } from 'ngx-toastr';

type SubmenuKey = 'admin' | 'txn';

@Component({
  selector: 'app-menu-bar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, NgClass, NgIf, SharedPipesModule],
  templateUrl: './menu-bar.component.html',
  styleUrls: ['./menu-bar.component.css'],
})
export class MenuBarComponent implements OnInit, OnDestroy {
  public readonly ModuleIds = ModuleIds;
  isHrUnlocked: boolean = false;
  private isBrowser: boolean;
  private lastKnownHrState: boolean = false;

  /** Sidebar collapsed state */
  isCollapsed: boolean = false;

  /** UI state, kept in a single object for clarity */
  state = {
    adminOpen: false,
    txnOpen: false,
  };

  private destroy$ = new Subject<void>();
  private statusCheckInterval: any;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly hrAuthServiceService: HrAuthServiceService,
    private readonly sidebarStateService: SidebarStateService,
    private readonly toaster: ToastrService,
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    // Set initial open states from current URL
    this.syncOpenState(this.router.url);

    // Keep submenus in sync on each navigation end
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((e) => this.syncOpenState(e.urlAfterRedirects || e.url));

    // Only run browser-specific code if we're in the browser
    if (this.isBrowser) {
      // Load sidebar collapsed state from localStorage
      const savedCollapsed = localStorage.getItem('sidebarCollapsed');
      this.isCollapsed = savedCollapsed === 'true';
      this.sidebarStateService.setCollapsed(this.isCollapsed);

      // Initial HR status check
      this.checkHrUnlockStatus();

      // Setup event listeners
      this.setupEventListeners();

      // Start periodic status checking
      this.startPeriodicStatusCheck();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.statusCheckInterval) {
      clearInterval(this.statusCheckInterval);
    }
  }

  private setupEventListeners(): void {
    if (!this.isBrowser) return;

    // Listen for HR status changes
    fromEvent(window, 'hrStatusChanged')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        const newStatus = event.detail?.isUnlocked || false;
        if (this.isHrUnlocked !== newStatus) {
        //  console.log('HR status changed via event:', newStatus);
          this.isHrUnlocked = newStatus;
          this.lastKnownHrState = newStatus;
        }
      });

    // Listen for storage changes
    fromEvent(window, 'storage')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (event.key === 'hrToken') {
          // Small delay to ensure the change is processed
          timer(100).subscribe(() => {
            this.checkHrUnlockStatus();
          });
        }
      });
  }

  private startPeriodicStatusCheck(): void {
    if (!this.isBrowser) return;

    // Check status periodically, but less aggressively
    this.ngZone.runOutsideAngular(() => {
      this.statusCheckInterval = setInterval(() => {
        // Only check if document is visible to save resources
        if (document.visibilityState === 'visible') {
          this.ngZone.run(() => {
            this.checkHrUnlockStatus();
          });
        }
      }, 10000); // Check every 10 seconds (reduced from 3s for better performance)
    });
  }

  /** Toggle sidebar collapse/expand */
  toggleSidebar(): void {
    this.isCollapsed = !this.isCollapsed;
    // Update the shared service
    this.sidebarStateService.setCollapsed(this.isCollapsed);

    // Save preference to localStorage
    if (this.isBrowser) {
      localStorage.setItem('sidebarCollapsed', String(this.isCollapsed));
    }
  }

  /**
   * Check if current user is a payroll employer (employerTypeId = 2)
   */
  isPayrollEmployer(): boolean {
    return this.authService.getEmployerTypeId() === 2;
  }

  /**
   * Check if the current user is eligible to access Payroll Management
   */
  isPayRollEligible(): boolean {
    return this.authService.getIsPayRollEligible();
  }

  onLockedPayrollClick(): void {
    this.toaster.warning('Payroll Management access is restricted for your account.');
  }

  /**
   * Check if a menu section should be restricted for current user
   */
  isMenuRestricted(menuSection: string): boolean {
    if (!this.isPayrollEmployer()) {
      return false;
    }

    const restrictedSections = ['transactions', 'general'];
    return restrictedSections.includes(menuSection.toLowerCase());
  }

  /** Toggle a submenu and ensure the other doesn't conflict */
  toggle(key: SubmenuKey): void {
    if (key === 'admin') {
      this.state.adminOpen = !this.state.adminOpen;
      if (this.state.adminOpen) this.state.txnOpen = false;
    } else {
      this.state.txnOpen = !this.state.txnOpen;
      if (this.state.txnOpen) this.state.adminOpen = false;
    }
  }

  /** Logout */
  logout(): void {
    // Clear HR session on logout
    this.clearHrSession();
    this.authService.logout();
  }

  /** Open the right submenu based on route */
  private syncOpenState(url: string): void {
    this.state.adminOpen = url.startsWith('/admin');
    this.state.txnOpen = url.startsWith('/transactions');
  }

  /**
   * Handle HR link click - either unlock or show lock confirmation
   */
  handleHrLinkClick(): void {
    if (!this.isBrowser) return;

    try {
      // Force a status check before deciding what to do
      this.checkHrUnlockStatus();

      if (this.isHrUnlocked) {
        // HR is currently unlocked, show lock confirmation
        this.navigateToHrWithLockIntent();
      } else {
        // HR is locked, go to unlock
        this.goToHrLink();
      }
    } catch (error) {
      // console.error('Error handling HR link click:', error);
    }
  }

  /**
   * Navigate to HR page with intent to show lock confirmation
   */
  private navigateToHrWithLockIntent(): void {
    if (!this.isBrowser) return;

    try {
      //console.log('Navigating to HR with lock intent');
      sessionStorage.setItem('showHrLockModal', 'true');

      const stateData = {
        isLoggedIn: true,
        showLockModal: true,
      };

      if (this.router.url === '/hr-link/login') {
        window.location.reload();
        history.replaceState(stateData, '');
      } else {
        // Not on login route, navigate and pass state
        this.router
          .navigate(['/hr-link/login'], { state: stateData })
          .catch((error) => {
          //  console.error('Navigation error:', error);
            this.showDirectLockConfirmation();
          });
      }
    } catch (error) {
      // console.error('Error navigating with lock intent:', error);
    }
  }

  private showDirectLockConfirmation(): void {
    const confirmed = confirm('Are you sure you want to lock HR access?');
    if (confirmed) {
      this.lockHrAccess();
    }
  }

  private lockHrAccess(): void {
    if (!this.isBrowser) return;

    try {
    //  console.log('Locking HR access from menu...');

      // Clear all HR-related data
      this.hrAuthServiceService.clearHrSession();
      this.hrAuthServiceService.setHrUnlocked(false);

      sessionStorage.removeItem('hrToken');
      sessionStorage.removeItem('hrEmployerId');
      localStorage.removeItem('hrToken');
      sessionStorage.removeItem('showHrLockModal');

      // Update local state immediately
      this.isHrUnlocked = false;
      this.lastKnownHrState = false;

      // Dispatch event for other components
      window.dispatchEvent(
        new CustomEvent('hrStatusChanged', {
          detail: { isUnlocked: false },
        })
      );

    //  console.log('HR access locked from menu');

      // Navigate to HR link to show unlock interface
      this.router.navigate(['/hr-link']).catch((error) => {
        //console.error('Navigation after lock failed:', error);
      });
    } catch (error) {
      //console.error('Error locking HR access:', error);
    }
  }

  goToHrLink(): void {
    if (!this.isBrowser) return;

    try {
      const userEmail = this.getUserEmail();

      //console.log('Going to HR link, email found:', !!userEmail);

      if (!userEmail) {
        //console.warn('No user email found, checking alternatives...');

        const alternativeEmail = this.getAlternativeEmail();

        if (alternativeEmail) {
          //console.log('Found alternative email, storing and proceeding');
          sessionStorage.setItem('email', alternativeEmail);
        } else {
          //console.error('No user email found in any storage location');
          // Still navigate, let the HR component handle the error
        }
      }

      this.navigateToHrLink();
    } catch (error) {
      //console.error('Error in goToHrLink:', error);
      this.navigateToHrLink();
    }
  }

  private getUserEmail(): string {
    if (!this.isBrowser) return '';

    return (
      sessionStorage.getItem('email') ||
      sessionStorage.getItem('userEmail') ||
      localStorage.getItem('email') ||
      localStorage.getItem('userEmail') ||
      ''
    );
  }

  private getAlternativeEmail(): string {
    if (!this.isBrowser) return '';

    const possibleKeys = [
      'email',
      'userEmail',
      'user_email',
      'loginEmail',
      'currentUserEmail',
      'authEmail',
    ];

    for (const key of possibleKeys) {
      const email = sessionStorage.getItem(key) || localStorage.getItem(key);
      if (email && email.includes('@')) {
        return email;
      }
    }

    return '';
  }

  private navigateToHrLink(): void {
    this.router.navigate(['/hr-link']).catch((error) => {
     // console.error('Navigation to HR link failed:', error);
    });
  }

  private clearHrSession(): void {
    if (!this.isBrowser) return;

    try {
      sessionStorage.removeItem('hrToken');
      sessionStorage.removeItem('hrEmployerId');
      sessionStorage.removeItem('showHrLockModal');
      localStorage.removeItem('hrToken');
      sessionStorage.removeItem('employerTypeId');
      localStorage.removeItem('employerTypeId');

      this.isHrUnlocked = false;
      this.lastKnownHrState = false;

      this.hrAuthServiceService.setHrUnlocked(false);

      //console.log('HR session cleared completely');
    } catch (error) {
      //console.error('Error clearing HR session:', error);
    }
  }

  /**
   * Check if HR is unlocked - more reliable version
   */
  checkHrUnlockStatus(): void {
    if (!this.isBrowser) return;

    try {
      // Check multiple sources for HR unlock status
      const hrToken =
        sessionStorage.getItem('hrToken') || localStorage.getItem('hrToken');
      const serviceStatus = this.hrAuthServiceService.isHrUnlocked();

      // HR is unlocked if we have a token AND service says it's unlocked
      const newStatus = !!(hrToken && serviceStatus);

      // Only update if status actually changed
      if (this.isHrUnlocked !== newStatus) {
        // console.log(
        //   'HR unlock status changed from',
        //   this.isHrUnlocked,
        //   'to',
        //   newStatus
        // );
        this.isHrUnlocked = newStatus;
        this.lastKnownHrState = newStatus;

        // If status changed to false, ensure everything is cleaned up
        if (!newStatus) {
          this.clearHrSession();
        }
      }
    } catch (error) {
     // console.error('Error checking HR unlock status:', error);
      // On error, assume locked for security
      this.isHrUnlocked = false;
      this.lastKnownHrState = false;
    }
  }

  goToHrDashboard(): void {
    if (this.authService.isLoggedIn()) {
      this.router.navigateByUrl('/hr-link/login');
    }
  }

}
